import { audio, Button, chartStore, Checkbox, Dropdown, LongShortStatistics, OrdersProps, SearchSelect, showNotification, TextInput, themeStore, Toggle, Tooltip } from "@root/common"
import { InputOutputBlockStyled, InputOutputRowStyled, SpaceBetweenRow, ToastMessageStyled } from "./OrdersStyled"
import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { observer } from "mobx-react-lite"
import { getOrdersPosition, OrderLogs, OrdersMargin, OrdersMode, sendOrder, SendOrderRes } from "../../api/orders.api"
import ArrowLeftIcon from '@mui/icons-material/ArrowLeft';
import { getGeneralTitles, getOrderPrices, getOrderPricesInCoins, getReverseOrderPrices, getReverseOrderPricesInCoins } from "../Graph/utils"
import { toast } from "react-toastify";
import { LinearProgress } from "@mui/material"
import {v4 as uuid} from "uuid"

const buttonStyles: React.CSSProperties = {
  width: 'calc(100% - 32px)',
  padding: '10px',
  marginTop: 'auto'
}

export interface OrdersFormProps {
  mode?: OrdersMode
  setMode: (mode?: OrdersMode) => void
}

const REPEAT_OPTIONS = ["1", "3", "5", "10"]
const INTERVAL_OPTIONS = ["1", "3", "5", "10"]

const titleMap: Record<OrdersMode, string> = {
  [OrdersMode.LIMIT]: "Лимитные ордера",
  [OrdersMode.MARKET]: "Маркет ордера",
  [OrdersMode.FAST]: "Быстрое открытие"
}

const IS_IN_COINS_OPTIONS = ["USDT ($)", "Монеты (₿)"]
const IS_IN_COINS_SELECTED = ["$", "₿"]

const LAST_DATA_TIME = "last-data-time"
const MAX_UNACTIVE_SECONDS = 15;
function checkIsDeprecated(key: string) {
  const curTime = Date.now()
  const lastTime = parseInt(sessionStorage.getItem(key) ?? '0')
  const diffSeconds = (curTime - lastTime) / 1e3
  return diffSeconds > MAX_UNACTIVE_SECONDS
}

export const OrdersForm: FC<OrdersProps & OrdersFormProps> = observer(({type, statistics, long, short, coin, mode, setMode}) => {
  const [margin, setMargin] = useState<OrdersMargin>(OrdersMargin.CROSS)
  const [leverage, setLeverage] = useState(1)
  const [inputIsInCoins, setInputIsInCoins] = useState(false)
  const [outputIsInCoins, setOutputIsInCoins] = useState(false)
  const [inputVolume, setInputVolume] = useState(0)
  const [_outputVolume, setOutputVolume] = useState(0)
  const [fullPositionVolume, setFullPositionVolume] = useState(0)
  const [input, setInput] = useState(0)
  const [output, setOutput] = useState(0)
  const [inputSlippage, setInputSlippage] = useState(0)
  const [outputSlippage, setOutputSlippage] = useState(0)
  const [isFullPosition, setIsFullPosition] = useState(false)
  const [delay, setDelay] = useState(0)
  const [liquidation, setLiquidation] = useState(false)
  const [inputRepeat, setInputRepeat] = useState(1)
  const [outputRepeat, setOutputRepeat] = useState(1)
  const [inputInterval, setInputInterval] = useState(1)
  const [outputInterval, setOutputInterval] = useState(1)

  useEffect(() => {
    if (isFullPosition) {
      setOutputRepeat(1)
      setOutputInterval(1)
    }
  }, [isFullPosition])

  const outputVolume = useMemo(() => {
    return isFullPosition ? fullPositionVolume : _outputVolume
  }, [_outputVolume, isFullPosition])

  let intervalId = useRef<undefined | NodeJS.Timer>();
  useEffect(() => {
    if (!long || !short || !mode || !isFullPosition) {
      return;
    }

    const makeRequest = async() => {
      try {
        const response = await getOrdersPosition({coin, long, short, type})
        typeof response.amount === 'number' && setFullPositionVolume(response.amount)
      } catch (err) {
      }
    }

    (async () => {
      await makeRequest();
      intervalId.current = setInterval(async () => {
        await makeRequest()
      }, 5 * 1e3)
    })()

    return () => {
      if (intervalId) {
        clearInterval(intervalId.current)
      }
    }

  }, [long, short, coin, type, mode, isFullPosition])

  const getMarginButtonStyles = useCallback((buttonValue: OrdersMargin) => {
    return {
      padding: '8px',
      width: '50%',
      border: `1px solid ${themeStore.gridColor}`,
      backgroundColor: buttonValue === margin ? themeStore.btnColor : themeStore.bgColor,
      color: buttonValue === margin ? "white" : themeStore.textColor,
    }
  }, [margin])

  const inputGeneralTitles: Record<string, string | null> = useMemo(() => {
      const titles = getGeneralTitles(type, statistics, inputVolume, inputIsInCoins)
      return titles
  }, [type, statistics, inputVolume, inputIsInCoins])
  const outputGeneralTitles: Record<string, string | null> = useMemo(() => {
      const titles = getGeneralTitles(type, statistics, outputVolume, outputIsInCoins || isFullPosition)
      return titles
  }, [type, statistics, outputVolume, fullPositionVolume, outputIsInCoins, isFullPosition])

  const [inputSubmited, setInputSubmited] = useState(false)
  const [outputSubmited, setOutputSubmited] = useState(false)

  const inputTimeout = useRef<NodeJS.Timeout>(undefined);
  const [inputCounter, setInputCounter] = useState(0)
  const inputDelay = useRef(delay)
  const waitNextInputIteration = useRef(false)

  const outputTimeout = useRef<NodeJS.Timeout>(undefined);
  const [outputCounter, setOutputCounter] = useState(0)
  const outputDelay = useRef(delay)
  const waitNextOutputIteration = useRef(false)

  useEffect(() => {
    inputDelay.current = delay
    outputDelay.current = delay
  }, [delay])

  const onInputCancel = () => {
    clearTimeout(inputTimeout.current)
    setInputSubmited(false)
    setInputCounter(0)
    inputTimeout.current = undefined
    waitNextInputIteration.current = false;
    inputDelay.current = delay
  }
  const onOutputCancel = () => {
    clearTimeout(outputTimeout.current)
    setOutputSubmited(false)
    setOutputCounter(0)
    outputTimeout.current = undefined
    waitNextOutputIteration.current = false;
    outputDelay.current = delay
  }

  const sendRequest = async (reverse?: boolean) => {
    if (!long || !short || !mode || !statistics) {
      return
    }
    if (chartStore.hasConnectionError[long] || chartStore.hasConnectionError[short]) {
      toast(<span><span style={{color: "red"}}>Плохое соединение: </span> отправка ордера отменена</span>)
      return
    }

    const volume = reverse ? outputVolume : inputVolume

    let orderPrices;
    if (reverse ? outputIsInCoins : inputIsInCoins) {
      orderPrices = !reverse ? getOrderPricesInCoins(statistics, volume) : getReverseOrderPricesInCoins(statistics, volume)
    } else {
      orderPrices = !reverse
      ? getOrderPrices(statistics, volume)
      : (isFullPosition
        ? getReverseOrderPricesInCoins(statistics, fullPositionVolume)
        : getReverseOrderPrices(statistics, volume)
      )
    }
    if (!orderPrices) {
      return;
    }

    const {long_price, short_price} = orderPrices

    let sendOrderResult: SendOrderRes | undefined;

    try {
      if (!reverse) {
        sendOrderResult = await sendOrder({
          type,
          long,
          short,
          coin,
          is_in_coins: inputIsInCoins,
          amount: volume,
          liquidation,
          settings: {
            type: mode,
            slippage: inputSlippage,
            leverage,
            long_price,
            short_price,
            margin
          },
          is_exit: false
        })
      } else {
        sendOrderResult = await sendOrder({
          type,
          long,
          short,
          coin,
          is_in_coins: outputIsInCoins,
          amount: volume,
          is_full_position: isFullPosition,
          liquidation,
          settings: {
            type: mode,
            slippage: outputSlippage,
            leverage,
            long_price,
            short_price,
            margin
          },
          is_exit: true
        })
      }
    } catch {}

    try {
      const response = await getOrdersPosition({coin, long, short, type})
      typeof response.amount === 'number' && setFullPositionVolume(response.amount)
    } catch (err) {}

    if (!sendOrderResult) {
      return false;
    }

    const longLogs = sendOrderResult[long]
    const shortLogs = sendOrderResult[short]

    if (
      JSON.stringify(longLogs).toLowerCase().includes("доступа нет") ||
      JSON.stringify(shortLogs).toLowerCase().includes("доступа нет")
    ) {
      onInputCancel();
      onOutputCancel();
    }

    const lonsShortStatistics = statistics as LongShortStatistics
    const longName = lonsShortStatistics.long.name ?? ''
    const shortName = lonsShortStatistics.short.name ?? ''
    toast(() =>
      <ToastMessage
        title={longName}
        logs={longLogs}
      />
    )
    toast(() =>
      <ToastMessage
        title={shortName}
        logs={shortLogs}
      />
    )

    let success = true;
    if (longLogs.errors.length === 0 && shortLogs.errors.length === 0) {
      showNotification({
        title: `${coin}\n${window.location.hostname}`,
        body: `${longName} - ${shortName}: Ордера созданы`
      })
    } else {
      success = false;
      showNotification({
        title: `${coin}\n${window.location.hostname}`,
        body: `${longName} - ${shortName}: Ошибка при открытии ордеров`
      })
    }

    audio.play()
    return success;
  }

  const inputLastDataKey = useMemo(() => LAST_DATA_TIME + uuid(), [])
  const outputLastDataKey = useMemo(() => LAST_DATA_TIME + uuid(), [])

  useEffect(() => {
    return () => {
      sessionStorage.removeItem(inputLastDataKey)
      sessionStorage.removeItem(outputLastDataKey)
    }
  }, [])

  useEffect(() => {
    if (!inputSubmited) {
      return;
    }

    if (checkIsDeprecated(inputLastDataKey)) {
      onInputCancel();
      onOutputCancel();
      showNotification({
        title: `${coin}\n${window.location.hostname}`,
        body: `Мониоринг отменен.`
      })
      return;
    }
    sessionStorage.setItem(inputLastDataKey, Date.now().toString())

    const generalTitlesInput = parseFloat(inputGeneralTitles["Вход"] || '') ?? -Infinity

    if (generalTitlesInput > input) {
      if (inputTimeout.current) {
        return;
      }

      inputTimeout.current = setTimeout(async () => {
        sendRequest().catch(e => null);
        waitNextInputIteration.current = true

        // const success = await successPromise
        // if (!success) {
        //   setTimeout(() => {
        //     waitNextInputIteration.current = false
        //     inputTimeout.current = undefined;
        //   }, 500) // чтобы убрать ддос
        //   return;
        // }

        if (inputCounter === inputRepeat - 1) {
          setInputSubmited(false)
          setInputCounter(0)
          inputDelay.current = delay
          inputTimeout.current = undefined;
          waitNextInputIteration.current = false;
        } else {
          setInputCounter(cur => cur + 1)
          if (delay > inputInterval) {
            inputDelay.current = inputInterval;
          }
          setTimeout(() => {
            waitNextInputIteration.current = false;
            inputTimeout.current = undefined;
          }, Math.max(0, inputInterval - delay) * 1e3)
        }
      }, inputDelay.current * 1e3)
    } else {
      inputDelay.current = delay
      clearTimeout(inputTimeout.current)
      if (!waitNextInputIteration.current) {
        inputTimeout.current = undefined
      }
    }
  }, [
    inputSubmited,
    inputGeneralTitles,
    input,
  ])

  useEffect(() => {
    if (!outputSubmited) {
      return;
    }

    if (checkIsDeprecated(outputLastDataKey)) {
      onInputCancel();
      onOutputCancel();
      showNotification({
        title: `${coin}\n${window.location.hostname}`,
        body: `Мониоринг отменен.`
      })
    }
    sessionStorage.setItem(outputLastDataKey, Date.now().toString())

    const generalTitlesOutput = parseFloat(outputGeneralTitles["Выход"] || '') ?? -Infinity

    if (generalTitlesOutput > output) {
      if (outputTimeout.current) {
        return
      }
      if (!fullPositionVolume) return

      outputTimeout.current = setTimeout(async () => {
        sendRequest(true).catch(e => null);
        waitNextOutputIteration.current = true;

        // const success = await successPromise
        // if (!success) {
        //   setTimeout(() => {
        //     waitNextOutputIteration.current = false;
        //     outputTimeout.current = undefined;
        //   }, 500) // чтобы убрать ддос
        //   return;
        // }

        if (outputCounter === outputRepeat - 1) {
          setOutputSubmited(false)
          setOutputCounter(0)
          outputDelay.current = delay
          outputTimeout.current = undefined
          waitNextOutputIteration.current = false;
        } else {
          setOutputCounter(cur => cur + 1)
          if (delay > outputInterval) {
            outputDelay.current = outputInterval;
          }
          setTimeout(() => {
            waitNextOutputIteration.current = false;
            outputTimeout.current = undefined;
          }, Math.max(0, outputInterval - delay) * 1e3)
        }
      }, outputDelay.current * 1e3)
    } else {
      outputDelay.current = delay
      clearTimeout(outputTimeout.current)
      if (!waitNextOutputIteration.current) {
        outputTimeout.current = undefined
      }
    }
  }, [
    outputSubmited,
    outputGeneralTitles,
    output,
  ])

  const onInputSubmit = () => {
    if (mode === OrdersMode.FAST) {
      sendRequest();
      return;
    }
    sessionStorage.setItem(inputLastDataKey, Date.now().toString())
    setInputSubmited(true)
    inputTimeout.current = undefined
  }

  const onOutputSubmit = () => {
    if (mode === OrdersMode.FAST) {
      sendRequest(true);
      return;
    }
    sessionStorage.setItem(outputLastDataKey, Date.now().toString())
    setOutputSubmited(true)
    outputTimeout.current = undefined;
  }

  useEffect(() => {
    onInputCancel()
  }, [inputRepeat, inputInterval, delay])

  useEffect(() =>{
    onOutputCancel()
  }, [outputRepeat, outputInterval, delay])


  if (!mode) {
    return null;
  }

  return (
    <>
      {mode && <ArrowLeftIcon
        fontSize="large"
        className="ordersBack"
        onClick={() => {
          setMode(undefined)
          onInputCancel()
          onOutputCancel();
        }}
      />}
      <b>{titleMap[mode]}</b>
      <SpaceBetweenRow>
        <p>Маржа, плечо</p>
        <SpaceBetweenRow style={{width: 'calc(60%)', justifyContent: 'end'}}>
          <Button style={getMarginButtonStyles(OrdersMargin.CROSS)} onClick={() => {setMargin(OrdersMargin.CROSS)}}>
            Кросс
          </Button>
          <Button style={getMarginButtonStyles(OrdersMargin.ISOLATED)} onClick={() => {setMargin(OrdersMargin.ISOLATED)}}>
            Изол
          </Button>
          <TextInput
            type="number"
            wrapperClassName="_input"
            style={{padding: '8px', backgroundColor: themeStore.bgColor, color: themeStore.textColor, borderColor: themeStore.gridColor}}
            value={leverage}
            setValue={setLeverage}
          />
        </SpaceBetweenRow>
      </SpaceBetweenRow>
      {mode !== OrdersMode.FAST &&
        <>
          <SpaceBetweenRow>
            <p>Задержка, сек <Tooltip title={"Задержка указывает, через сколько времени после соблюдения условия по спреду в процентах происходит выставление ордеров."}/></p>
            <Dropdown btnStyle={{borderColor: themeStore.gridColor}} className="delay" value={delay.toString()} setValue={val => setDelay(parseInt(val, 10))} options={["0", "1", "2", "3", "4", "5"]}/>
          </SpaceBetweenRow>
        </>
      }
        <InputOutputRowStyled>
          <InputOutputBlockStyled
            style={{
              border: `1px solid ${themeStore.textColor}`,
              borderLeft: 'none'
            }}
          >
            <b>Вход</b>
            <SpaceBetweenRow>
              <p>Объем</p>
              <span style={{
                position: 'relative',
                display: 'flex',
                justifyContent: 'end',
              }}>
                <TextInput
                  type="number"
                  wrapperClassName="input volume_input"
                  value={inputVolume}
                  setValue={setInputVolume}
                />
                <Dropdown
                  value={IS_IN_COINS_SELECTED[Number(inputIsInCoins)]}
                  setValue={(val) => {setInputIsInCoins(val === IS_IN_COINS_OPTIONS[1])}}
                  options={IS_IN_COINS_OPTIONS}
                  btnStyle={{
                    border: 'none',
                    margin: '1px',
                  }}
                  className="is_in_coins"
                />
              </span>
            </SpaceBetweenRow>
            {mode !== OrdersMode.FAST &&
              <SpaceBetweenRow>
                <p>Вход, %</p>
                <TextInput
                  type="number"
                  wrapperClassName="input"
                  value={input}
                  setValue={setInput}
                />
              </SpaceBetweenRow>
            }
            {mode === OrdersMode.LIMIT &&
              <SpaceBetweenRow>
                <p>Slippage, %</p>
                <TextInput
                  type="number"
                  wrapperClassName="input"
                  value={inputSlippage}
                  setValue={setInputSlippage}
                />
              </SpaceBetweenRow>
            }
            {mode !== OrdersMode.FAST &&
              <>
                <SpaceBetweenRow>
                  <p>Повторений</p>
                  <Dropdown
                    btnStyle={{borderColor: themeStore.gridColor}}
                    value={inputRepeat.toString()}
                    setValue={val => setInputRepeat(parseInt(val))}
                    options={REPEAT_OPTIONS}
                  />
                </SpaceBetweenRow>
                <SpaceBetweenRow>
                  <p>Интервал</p>
                  <Dropdown
                    btnStyle={{borderColor: themeStore.gridColor}}
                    value={inputInterval.toString()}
                    setValue={val => setInputInterval(parseInt(val))}
                    options={INTERVAL_OPTIONS}
                  />
                </SpaceBetweenRow>
              </>
            }
            <SpaceBetweenRow>
              <p>Ликвидация</p>
              <Checkbox value={liquidation} setValue={setLiquidation}/>
            </SpaceBetweenRow>
            {inputSubmited
              ?
                <>
                  <Button style={buttonStyles} onClick={onInputCancel} disabled={!inputSubmited}>
                    Отменить
                  </Button>
                  Осталось ордеров: {inputRepeat - inputCounter}
                  <LinearProgress style={{width: '100%'}}/>
                </>

              :
                <Button style={buttonStyles} onClick={onInputSubmit} disabled={inputSubmited}>
                  Применить
                </Button>
            }
          </InputOutputBlockStyled>
          <InputOutputBlockStyled
            style={{
              border: `1px solid ${themeStore.textColor}`,
              borderRight: 'none',
              borderLeft: 'none',
            }}
          >
              <b>Выход</b>
              <SpaceBetweenRow>
                <p>Объем</p>
                  <span style={{
                    position: 'relative',
                    display: 'flex',
                    justifyContent: 'end',
                  }}>
                    <TextInput
                      disabled={isFullPosition}
                      type="number"
                      wrapperClassName="input volume_input"
                      value={_outputVolume}
                      setValue={setOutputVolume}
                    />
                    <Dropdown
                      value={IS_IN_COINS_SELECTED[Number(outputIsInCoins)]}
                      setValue={(val) => {setOutputIsInCoins(val === IS_IN_COINS_OPTIONS[1])}}
                      options={IS_IN_COINS_OPTIONS}
                      btnStyle={{
                        border: 'none',
                        margin: '1px',
                      }}
                      className="is_in_coins"
                      disabled={isFullPosition}
                    />
                  </span>

              </SpaceBetweenRow>
              {mode !== OrdersMode.FAST &&
                <SpaceBetweenRow>
                  <p>Выход, %</p>
                  <TextInput
                    type="number"
                    wrapperClassName="input"
                    value={output}
                    setValue={setOutput}
                  />
                </SpaceBetweenRow>
              }
              {mode === OrdersMode.LIMIT &&
                <SpaceBetweenRow>
                  <p>Slippage, %</p>
                  <TextInput
                    type="number"
                    wrapperClassName="input"
                    value={outputSlippage}
                    setValue={setOutputSlippage}
                  />
                </SpaceBetweenRow>
              }
              {mode !== OrdersMode.FAST &&
                <>
                  <SpaceBetweenRow>
                    <p>Повторений</p>
                    <Dropdown
                      btnStyle={{borderColor: themeStore.gridColor}}
                      value={outputRepeat.toString()}
                      setValue={val => setOutputRepeat(parseInt(val))}
                      options={REPEAT_OPTIONS}
                      disabled={isFullPosition}
                    />
                  </SpaceBetweenRow>
                  <SpaceBetweenRow>
                    <p>Интервал</p>
                    <Dropdown
                      btnStyle={{borderColor: themeStore.gridColor}}
                      value={outputInterval.toString()}
                      setValue={val => setOutputInterval(parseInt(val))}
                      options={INTERVAL_OPTIONS}
                      disabled={isFullPosition}
                    />
                  </SpaceBetweenRow>
                </>
              }
              <SpaceBetweenRow>
                <p>Вся позиция</p>
                <Checkbox value={isFullPosition} setValue={setIsFullPosition}/>
              </SpaceBetweenRow>
            <>
              {outputSubmited
                ?
                  <>
                    <Button style={buttonStyles} onClick={onOutputCancel} disabled={!outputSubmited}>
                      Отменить
                    </Button>
                    Осталось ордеров: {outputRepeat - outputCounter}
                    <LinearProgress style={{width: '100%'}}/>
                  </>

                :
                  <Button style={buttonStyles} onClick={onOutputSubmit} disabled={outputSubmited}>
                    Применить
                  </Button>
              }
            </>
          </InputOutputBlockStyled>
        </InputOutputRowStyled>
    </>
  )
})


interface ToastMessageProps {
  title: string
  logs: OrderLogs
}

export const ToastMessage =({logs, title}: ToastMessageProps) => {
  return (
    <ToastMessageStyled>
      <h3>{title}</h3>
      {logs?.errors.map(err => (
        <div className="toastLog errorLog">
          <b style={{color: "red"}}>{err.type}: </b><span dangerouslySetInnerHTML={{ __html: err.message }}></span>
        </div>
      ))}
      {logs?.success_logs.map(log => (
        <div className="toastLog successLog">
          <b style={{color: "green"}}>{log.type}: </b>{log.message}
        </div>
      ))}
    </ToastMessageStyled>
  )
}
